/* AI SUMMARY OF A REPORT PAGE — one panel for the Sales Report and the Data Explorer (Max, 2026-10-24: "AI sẽ nhận xét
   data chỉ ra highlight và low light cũng như là hướng để detect sâu hơn cho người xem báo cáo"). The server works the
   figures out and the AI words them (reportInsights.js); this only asks and draws.
   NOTHING IS ASKED UNTIL SOMEBODY OPENS IT — a summary costs a call, and most visits to a report do not want one. Once
   open it follows the page: a new week, filter or page asks again (an answer for the same figures comes from the
   server's cache), and an answer that arrives for a page that has since changed is dropped. Open or shut is remembered
   per screen. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUp, RefreshCw, TrendingDown, TrendingUp, Search } from 'lucide-react';
import AiTag from './AiTag';
import './AiInsightPanel.css';

/* Staging only for now (REACT_APP_INSIGHTS; the backend half is REPORT_INSIGHTS=1). */
export const INSIGHTS_ON = process.env.REACT_APP_INSIGHTS === '1';
/* what a build without the flag carries in its place — deploy_production.sh looks for it */
export const INSIGHTS_OFF_MARK = INSIGHTS_ON ? '' : '__insights_off';

const readOpen = (key) => { try { return localStorage.getItem(key) === '1'; } catch { return false; } };
const writeOpen = (key, v) => { try { localStorage.setItem(key, v ? '1' : '0'); } catch { /* private window */ } };

/* askKey: what the page shows (null = nothing to summarise yet, with `blocked` saying why); ask({ refresh }) → the answer */
export default function AiInsightPanel({ askKey, ask, blocked, storageKey }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(() => readOpen(storageKey));
  const [state, setState] = useState({ busy: false, data: null, error: null, unavailable: false, key: null });
  const seq = useRef(0);
  const askRef = useRef(ask);
  askRef.current = ask;
  const lang = String(i18n.resolvedLanguage || i18n.language || 'en').slice(0, 2);

  const run = useCallback(async (refresh) => {
    if (!askKey) return;
    const my = ++seq.current;
    setState((s) => ({ ...s, busy: true, error: null, unavailable: false }));
    try {
      const data = await askRef.current({ refresh, lang });
      if (my === seq.current) setState({ busy: false, data, error: null, unavailable: false, key: `${askKey}|${lang}` });
    } catch (e) {
      if (my === seq.current) setState((s) => ({ ...s, busy: false, error: e.message, unavailable: !!e.unavailable }));
    }
  }, [askKey, lang]);

  /* follow the page while open (a short wait, so a run of filter clicks asks once) */
  useEffect(() => {
    if (!open || !askKey || state.key === `${askKey}|${lang}`) return undefined;
    const timer = setTimeout(() => run(false), 500);
    return () => clearTimeout(timer);
  }, [open, askKey, lang, run, state.key]);

  const toggle = (v) => { setOpen(v); writeOpen(storageKey, v); };
  const time = (iso) => { try { return new Date(iso).toLocaleString(i18n.language, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };

  if (!open) {
    return (
      <div className="aiin aiin-closed">
        <button type="button" className="aiin-open" onClick={() => toggle(true)} disabled={!askKey} title={!askKey ? blocked || t('xInsight.nothingYet') : undefined}>
          <AiTag size={16} /> <span>{t('xInsight.open')}</span>
        </button>
        <span className="aiin-hint">{askKey ? t('xInsight.hint') : blocked || t('xInsight.nothingYet')}</span>
      </div>
    );
  }
  const d = state.data;
  const stale = d && state.key !== `${askKey}|${lang}`;
  return (
    <section className={`aiin${state.busy ? ' busy' : ''}`} aria-live="polite" data-testid="ai-insight">
      <header className="aiin-head">
        <AiTag size={18} />
        <h3>{t('xInsight.title')}</h3>
        {d && !state.busy && <span className="aiin-when">{d.cached ? t('xInsight.fromCache', { time: time(d.at) }) : t('xInsight.written', { time: time(d.at) })}</span>}
        <span className="aiin-grow" />
        <button type="button" className="aiin-tool" onClick={() => run(true)} disabled={state.busy || !askKey} title={t('xInsight.rewrite')}>
          <RefreshCw size={14} /> <span>{t('xInsight.rewrite')}</span>
        </button>
        <button type="button" className="aiin-tool" onClick={() => toggle(false)} title={t('xInsight.hide')} aria-label={t('xInsight.hide')}>
          <ChevronUp size={15} />
        </button>
      </header>
      {!askKey && <p className="aiin-msg">{blocked || t('xInsight.nothingYet')}</p>}
      {askKey && state.busy && <p className="aiin-msg aiin-writing"><span className="aiin-dot" />{stale ? t('xInsight.stale') : t('xInsight.writing')}</p>}
      {askKey && state.error && !state.busy && (
        <p className="aiin-msg aiin-error">{t(state.unavailable ? 'xInsight.unavailable' : 'xInsight.failed', { reason: state.error })}</p>
      )}
      {askKey && d && (
        <div className={`aiin-body${state.busy ? ' dim' : ''}`}>
          <p className="aiin-headline">{d.headline}</p>
          <div className="aiin-cols">
            <List kind="hi" icon={<TrendingUp size={15} />} title={t('xInsight.highlights')} items={d.highlights} />
            <List kind="lo" icon={<TrendingDown size={15} />} title={t('xInsight.lowlights')} items={d.lowlights} />
            <List kind="dig" icon={<Search size={15} />} title={t('xInsight.dig')} items={d.dig} />
          </div>
          <p className="aiin-note">{t('xInsight.note')}</p>
        </div>
      )}
    </section>
  );
}

function List({ kind, icon, title, items }) {
  return (
    <div className={`aiin-list aiin-${kind}`}>
      <h4>{icon}<span>{title}</span></h4>
      <ul>
        {(items || []).map((x, i) => (
          <li key={i}><b>{x.title}</b><span>{x.detail}</span></li>
        ))}
      </ul>
    </div>
  );
}

/* the one way both screens ask: POST, JSON back, the server's own sentence on a refusal */
export const askInsight = async (path, body) => {
  const res = await fetch(path, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(data.error || `HTTP ${res.status}`); e.unavailable = !!data.unavailable; throw e; }
  return data;
};
